import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/table/data-table";
import { InviteModal, SuccessModal } from "../../components/modal";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const ROLES_CACHE_KEY = "cehr_company_roles_v1";

const Roles = () => {
  const cachedData = useMemo(
    () => readSessionCache(ROLES_CACHE_KEY, { roles: [] }),
    [],
  );
  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [roles, setRoles] = useState(cachedData?.roles || []);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [createRoleDepartmentId, setCreateRoleDepartmentId] = useState("");
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [isLoading, setIsLoading] = useState(!cachedData?.roles?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const openModal = () => {
    setCreateRoleDepartmentId(selectedDepartmentId || "");
    setOpenRoleModal(true);
  };
  const apiBaseUrl = getApiBaseUrl();

  const loadRoles = useCallback(
    async ({ silent = false, departmentId } = {}) => {
      if (!apiBaseUrl) {
        setErrorMessage("API base URL is not configured.");
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(`${apiBaseUrl}/organizations/roles`, {
          params: departmentId ? { departmentId } : {},
        });
        const nextRoles = response?.data?.roles || [];
        setRoles(nextRoles);
        writeSessionCache(ROLES_CACHE_KEY, { roles: nextRoles });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message ||
          "Unable to load roles. Please try again.";
        setErrorMessage(
          Array.isArray(responseMessage)
            ? responseMessage.join(", ")
            : responseMessage,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl],
  );

  const loadDepartments = useCallback(async () => {
    if (!apiBaseUrl) {
      setIsDepartmentsLoading(false);
      return;
    }

    try {
      setIsDepartmentsLoading(true);
      const response = await axios.get(`${apiBaseUrl}/organizations/departments`);
      setDepartments(response?.data?.departments || []);
    } catch (_error) {
      setDepartments([]);
    } finally {
      setIsDepartmentsLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    loadRoles({ silent: true, departmentId: selectedDepartmentId || undefined });
  }, [loadRoles, selectedDepartmentId]);

  useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    if (!selectedDepartmentId && departments.length > 0) {
      setSelectedDepartmentId("");
    }
  }, [departments, selectedDepartmentId]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadRoles({ silent: true, departmentId: selectedDepartmentId || undefined });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadRoles]);

  const resetForm = () => {
    setRoleName("");
    setRoleDescription("");
    setCreateRoleDepartmentId("");
    setErrorMessage("");
  };

  const closeRoleModal = () => {
    setOpenRoleModal(false);
    resetForm();
  };

  const createHandleSuccess = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    if (!roleName.trim() || !roleDescription.trim() || !createRoleDepartmentId) {
      setErrorMessage("Department, role name, and role description are required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.post(`${apiBaseUrl}/organizations/roles`, {
        roleName: roleName.trim(),
        roleDescription: roleDescription.trim(),
        departmentId: createRoleDepartmentId,
      });

      closeRoleModal();
      setOpenSuccessModal(true);
      await loadRoles({ departmentId: selectedDepartmentId || undefined });
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create role. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  let navigate = useNavigate();

  const headings = ["Role Name", "Role Description", "Department", "Actions"];
  const columns = ["roleName", "roleDescription", "departmentName"];

  return (
    <div className="dashboard-enter p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Roles" },
        ]}
        title="Roles"
        subtitle="Create roles used across employee permissions and hierarchy."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <div className="flex flex-col items-start gap-2">
            <button
              onClick={openModal}
              disabled={isDepartmentsLoading || departments.length === 0}
              title={
                isDepartmentsLoading
                  ? "Loading departments..."
                  : departments.length === 0
                  ? "Add a department first before creating a role"
                  : "Add a new role"
              }
              className="bg-[#1234CD] px-6 py-2 rounded-xl text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Add new
            </button>
            {isDepartmentsLoading ? (
              <p className="text-xs text-gray-500">Loading departments, please wait before creating a role.</p>
            ) : departments.length === 0 ? (
              <p className="text-xs text-gray-500">No departments available yet. Create a department first to add a role.</p>
            ) : null}
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <label className="text-sm font-medium text-[#0F172A]">Filter by department</label>
        <select
          value={selectedDepartmentId}
          onChange={(event) => setSelectedDepartmentId(event.target.value)}
          className="rounded-2xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A]"
        >
          <option value="">All departments</option>
          {departments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.departmentName || department.name}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        title=""
        headings={headings}
        columns={columns}
        data={roles}
        showEmptyState={!isLoading}
        emptyMessage="No roles found. Create one to get started."
        onView={(row) => {
          sessionStorage.setItem("selectedRoleId", row.id);
          navigate("/company-settings/roleview-details", {
            state: { roleId: row.id },
          });
        }}
      />

      <InviteModal
        isOpen={openRoleModal}
        onClose={closeRoleModal}
      >
        <div className="my-8">
          <div>
            <p className=" text-2xl font-semibold text-black font-Outfit">
              Create Role
            </p>
          </div>
          <form
            className="my-12"
            onSubmit={createHandleSuccess}
          >
            {errorMessage && (
              <p
                className="mb-4 text-sm text-red-600 dashboard-fade-in"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="role-department"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Department
              </label>
              <select
                id="role-department"
                value={createRoleDepartmentId}
                onChange={(ev) => setCreateRoleDepartmentId(ev.target.value)}
                required
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                disabled={departments.length === 0}
              >
                <option value="" disabled>
                  {departments.length > 0 ? "Select department" : "No departments available"}
                </option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.departmentName || department.name}
                  </option>
                ))}
              </select>
              {departments.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No departments are currently available. Add a department first or refresh the page.
                </p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="role-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Role Name
              </label>
              <input
                type="text"
                id="role-name"
                placeholder="Title"
                value={roleName}
                onChange={(ev) => setRoleName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="role-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Role Description
              </label>

              <textarea
                id="role-description"
                placeholder="Description"
                value={roleDescription}
                onChange={(ev) => setRoleDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="flex justify-center mt-20 items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] font-satoshi leading-[21.6px] w-40 rounded-xl text-white py-[16px] bg-[#1234CD] h-[40px]text-[16px] disabled:opacity-60 disabled:cursor-not-allowed dashboard-button-hover"
              >
                <span>{isSubmitting ? "Creating..." : "Create"}</span>
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />

      {isLoading && roles.length === 0 ? (
        <FetchLoadingState message="Loading roles..." />
      ) : null}
    </div>
  );
};

export default Roles;
